import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { 
  ArrowLeft, Search, Book, FileText, Video, HelpCircle, ChevronRight, 
  Palette, Code, Megaphone, DollarSign, Rocket, Settings, PlayCircle,
  ExternalLink, ThumbsUp, ThumbsDown, MessageCircle
} from 'lucide-react';
import { cn } from '../../lib/utils';
import api from '../../lib/axios';

// Knowledge base categories
const CATEGORIES = [
  { 
    id: 'getting-started', 
    title: 'Premiers pas', 
    icon: Rocket, 
    color: 'from-cyan-500 to-blue-600',
    description: 'Découvrez comment démarrer avec nos services'
  },
  { 
    id: 'design', 
    title: 'Design & Branding', 
    icon: Palette, 
    color: 'from-pink-500 to-rose-600',
    description: 'Tout sur la création d\'identité visuelle'
  },
  { 
    id: 'web', 
    title: 'Web & Digital', 
    icon: Code, 
    color: 'from-emerald-500 to-green-600',
    description: 'Sites web, applications et solutions digitales'
  },
  { 
    id: 'marketing', 
    title: 'Marketing', 
    icon: Megaphone, 
    color: 'from-amber-500 to-orange-600',
    description: 'Stratégies et campagnes marketing'
  },
  { 
    id: 'pricing', 
    title: 'Tarifs & Facturation', 
    icon: DollarSign, 
    color: 'from-purple-500 to-violet-600',
    description: 'Comprendre nos tarifs et modalités'
  },
  { 
    id: 'account', 
    title: 'Mon compte', 
    icon: Settings, 
    color: 'from-slate-500 to-slate-700',
    description: 'Gérer votre espace client'
  },
];

// Knowledge base articles
const ARTICLES = [
  // Getting Started
  {
    id: 1,
    slug: 'bienvenue-msc-agence',
    categoryId: 'getting-started',
    title: 'Bienvenue chez MSC L\'Agence',
    excerpt: 'Découvrez qui nous sommes et comment nous pouvons vous accompagner dans votre communication.',
    content: `
      <h2>Qui sommes-nous ?</h2>
      <p>MSC L'Agence est une agence de communication digitale spécialisée dans la création de marques fortes et de stratégies digitales performantes.</p>
      
      <h2>Nos services</h2>
      <ul>
        <li><strong>Identité visuelle</strong> : Logo, charte graphique, supports de communication</li>
        <li><strong>Web & Digital</strong> : Sites web, applications, e-commerce</li>
        <li><strong>Production vidéo</strong> : Films corporate, motion design, publicités</li>
        <li><strong>Marketing digital</strong> : SEO, réseaux sociaux, publicité en ligne</li>
      </ul>
      
      <h2>Nos valeurs</h2>
      <p>Créativité, Excellence, Transparence et Accompagnement personnalisé.</p>
    `,
    videoUrl: null,
    views: 1250,
    helpful: 45,
    notHelpful: 2
  },
  {
    id: 2,
    slug: 'comment-demarrer-projet',
    categoryId: 'getting-started',
    title: 'Comment démarrer un projet ?',
    excerpt: 'Les étapes pour lancer votre premier projet avec notre agence.',
    content: `
      <h2>Étape 1 : Prise de contact</h2>
      <p>Contactez-nous via notre formulaire ou prenez rendez-vous pour un appel découverte gratuit.</p>
      
      <h2>Étape 2 : Brief et devis</h2>
      <p>Nous analysons votre besoin et vous envoyons un devis détaillé sous 48h.</p>
      
      <h2>Étape 3 : Validation et lancement</h2>
      <p>Une fois le devis validé et l'acompte reçu, nous démarrons la production.</p>
      
      <h2>Étape 4 : Suivi et livraison</h2>
      <p>Suivez l'avancement de votre projet depuis votre espace client et validez les étapes clés.</p>
    `,
    videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
    views: 890,
    helpful: 38,
    notHelpful: 1
  },
  {
    id: 3,
    slug: 'programme-copilote',
    categoryId: 'getting-started',
    title: 'Le programme COPILOTE',
    excerpt: 'Tout savoir sur notre offre d\'accompagnement premium avec ROI garanti.',
    content: `
      <h2>Qu'est-ce que COPILOTE ?</h2>
      <p>COPILOTE est notre programme d'accompagnement premium conçu pour les entreprises qui veulent maximiser leur retour sur investissement marketing.</p>
      
      <h2>Les avantages</h2>
      <ul>
        <li>Stratégie sur-mesure avec objectifs chiffrés</li>
        <li>Équipe dédiée disponible</li>
        <li>Reporting mensuel détaillé</li>
        <li>ROI garanti contractuellement</li>
      </ul>
      
      <h2>Pour qui ?</h2>
      <p>PME et ETI souhaitant structurer leur communication digitale avec un partenaire de confiance.</p>
    `,
    videoUrl: null,
    views: 567,
    helpful: 29,
    notHelpful: 0
  },
  // Design
  {
    id: 4,
    slug: 'creer-logo-impactant',
    categoryId: 'design',
    title: 'Créer un logo impactant',
    excerpt: 'Les clés pour un logo mémorable et efficace.',
    content: `
      <h2>Les caractéristiques d'un bon logo</h2>
      <p>Un logo efficace doit être simple, mémorable, intemporel, polyvalent et approprié à votre secteur.</p>
      
      <h2>Notre processus créatif</h2>
      <ul>
        <li>Analyse de votre marque et de votre marché</li>
        <li>Recherches créatives et moodboards</li>
        <li>Propositions de concepts (3 minimum)</li>
        <li>Affinage et finalisation</li>
        <li>Livraison des fichiers sources</li>
      </ul>
    `,
    videoUrl: null,
    views: 432,
    helpful: 22,
    notHelpful: 1
  },
  {
    id: 5,
    slug: 'charte-graphique',
    categoryId: 'design',
    title: 'Qu\'est-ce qu\'une charte graphique ?',
    excerpt: 'Comprendre l\'importance d\'une charte graphique pour votre marque.',
    content: `
      <h2>Définition</h2>
      <p>La charte graphique est le document de référence qui définit l'ensemble des règles d'utilisation de votre identité visuelle.</p>
      
      <h2>Ce qu'elle contient</h2>
      <ul>
        <li>Utilisation du logo</li>
        <li>Palette de couleurs</li>
        <li>Typographies</li>
        <li>Iconographie et visuels</li>
        <li>Règles de mise en page</li>
      </ul>
    `,
    videoUrl: null,
    views: 289,
    helpful: 18,
    notHelpful: 0
  },
  // Web
  {
    id: 6,
    slug: 'types-sites-web',
    categoryId: 'web',
    title: 'Les différents types de sites web',
    excerpt: 'Site vitrine, e-commerce, application... Quel format choisir ?',
    content: `
      <h2>Site vitrine</h2>
      <p>Idéal pour présenter votre activité et vos services. Parfait pour les TPE/PME.</p>
      
      <h2>Site e-commerce</h2>
      <p>Pour vendre vos produits en ligne. Nous travaillons avec Shopify, WooCommerce et des solutions sur-mesure.</p>
      
      <h2>Application web</h2>
      <p>Pour des besoins métier spécifiques : CRM, gestion de projets, outils internes...</p>
    `,
    videoUrl: null,
    views: 521,
    helpful: 31,
    notHelpful: 2
  },
  // Pricing
  {
    id: 7,
    slug: 'systeme-wallet',
    categoryId: 'pricing',
    title: 'Le système Wallet',
    excerpt: 'Comment fonctionne notre système de wallet prépayé.',
    content: `
      <h2>Principe du Wallet</h2>
      <p>Le wallet est un système de crédits prépayés qui vous permet de consommer nos services de manière flexible.</p>
      
      <h2>Avantages</h2>
      <ul>
        <li>Tarif horaire préférentiel</li>
        <li>Flexibilité d'utilisation</li>
        <li>Suivi en temps réel</li>
        <li>Pas d'engagement longue durée</li>
      </ul>
      
      <h2>Comment recharger ?</h2>
      <p>Rechargez votre wallet directement depuis votre espace client par carte bancaire.</p>
    `,
    videoUrl: null,
    views: 678,
    helpful: 42,
    notHelpful: 1
  },
  {
    id: 8,
    slug: 'modalites-paiement',
    categoryId: 'pricing',
    title: 'Modalités de paiement',
    excerpt: 'Nos différentes options de paiement et délais.',
    content: `
      <h2>Modes de paiement acceptés</h2>
      <ul>
        <li>Carte bancaire (Visa, Mastercard)</li>
        <li>Virement bancaire</li>
        <li>Prélèvement automatique (abonnements)</li>
      </ul>
      
      <h2>Échéancier standard</h2>
      <p>30% à la commande, 40% à mi-projet, 30% à la livraison.</p>
    `,
    videoUrl: null,
    views: 345,
    helpful: 19,
    notHelpful: 0
  },
];

// Popular/Featured articles
const FEATURED_ARTICLES = [2, 3, 7];

const KnowledgeBasePage = () => {
  const navigate = useNavigate();
  const { articleSlug } = useParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [feedbackGiven, setFeedbackGiven] = useState({});
  const [clientPortal, setClientPortal] = useState({ url: '/login', label: 'Espace client', openInNewTab: true });

  // Fetch client portal settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await api.get('/cms/site-settings');
        if (response.data?.clientPortal) {
          setClientPortal(response.data.clientPortal);
        }
      } catch (error) {
        console.log('Using default client portal settings');
      }
    };
    fetchSettings();
  }, []);

  // If viewing a single article
  const currentArticle = articleSlug ? ARTICLES.find(a => a.slug === articleSlug) : null;

  const handleFeedback = (articleId, isHelpful) => {
    setFeedbackGiven(prev => ({ ...prev, [articleId]: isHelpful }));
    // In production, send this to the backend
  };

  // Filter articles by search
  const searchResults = searchQuery 
    ? ARTICLES.filter(article => 
        article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.excerpt.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  // Get articles by category
  const getArticlesByCategory = (categoryId) => {
    return ARTICLES.filter(a => a.categoryId === categoryId);
  };

  const getCategoryConfig = (categoryId) => {
    return CATEGORIES.find(c => c.id === categoryId);
  };

  // Single Article View
  if (currentArticle) {
    const category = getCategoryConfig(currentArticle.categoryId);
    const relatedArticles = ARTICLES
      .filter(a => a.id !== currentArticle.id && a.categoryId === currentArticle.categoryId)
      .slice(0, 3);

    return (
      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <header className="bg-white border-b sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
            <button 
              onClick={() => navigate('/site/aide')}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour à l'aide
            </button>
            <Link to="/site" className="font-bold text-lg text-slate-900">MSC L'AGENCE</Link>
          </div>
        </header>

        {/* Breadcrumb */}
        <div className="bg-white border-b">
          <div className="max-w-4xl mx-auto px-4 py-3">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Link to="/site/aide" className="hover:text-slate-700">Base de connaissances</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to={`/site/aide?category=${currentArticle.categoryId}`} className="hover:text-slate-700">
                {category?.title}
              </Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-slate-700">{currentArticle.title}</span>
            </div>
          </div>
        </div>

        {/* Article Content */}
        <article className="max-w-4xl mx-auto px-4 py-12">
          {/* Category Badge */}
          <Badge className={cn("text-white mb-4 bg-gradient-to-r", category?.color)}>
            {category?.title}
          </Badge>

          {/* Title */}
          <h1 className="text-3xl font-bold text-slate-900 mb-4">
            {currentArticle.title}
          </h1>
          
          <p className="text-lg text-slate-600 mb-8">
            {currentArticle.excerpt}
          </p>

          {/* Video if available */}
          {currentArticle.videoUrl && (
            <div className="aspect-video rounded-xl overflow-hidden bg-slate-200 mb-8">
              <iframe 
                src={currentArticle.videoUrl}
                className="w-full h-full"
                allowFullScreen
                title={currentArticle.title}
              />
            </div>
          )}

          {/* Content */}
          <div 
            className="prose prose-slate max-w-none"
            dangerouslySetInnerHTML={{ __html: currentArticle.content }}
          />

          {/* Feedback */}
          <div className="mt-12 pt-8 border-t">
            <p className="text-slate-600 mb-4">Cet article vous a-t-il été utile ?</p>
            {feedbackGiven[currentArticle.id] === undefined ? (
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => handleFeedback(currentArticle.id, true)}
                  className="gap-2"
                >
                  <ThumbsUp className="w-4 h-4" />
                  Oui, merci !
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => handleFeedback(currentArticle.id, false)}
                  className="gap-2"
                >
                  <ThumbsDown className="w-4 h-4" />
                  Non, pas vraiment
                </Button>
              </div>
            ) : (
              <p className="text-emerald-600 font-medium">
                Merci pour votre retour !
              </p>
            )}
          </div>

          {/* Contact Support */}
          <Card className="mt-8 bg-cyan-50 border-cyan-200">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-cyan-500 flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-slate-900">Besoin d'aide supplémentaire ?</h4>
                <p className="text-sm text-slate-600">Notre équipe support est disponible pour répondre à vos questions.</p>
              </div>
              <Button asChild className="bg-cyan-500 hover:bg-cyan-600">
                <Link to="/site#contact">Nous contacter</Link>
              </Button>
            </CardContent>
          </Card>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="bg-white py-12 border-t">
            <div className="max-w-4xl mx-auto px-4">
              <h3 className="text-xl font-bold text-slate-900 mb-6">Articles connexes</h3>
              <div className="grid md:grid-cols-3 gap-4">
                {relatedArticles.map(article => (
                  <Link 
                    key={article.id}
                    to={`/site/aide/${article.slug}`}
                    className="p-4 rounded-xl border border-slate-200 hover:border-cyan-300 hover:shadow-md transition-all group"
                  >
                    <h4 className="font-semibold text-slate-900 group-hover:text-cyan-600 transition-colors">
                      {article.title}
                    </h4>
                    <p className="text-sm text-slate-500 mt-1 line-clamp-2">{article.excerpt}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Footer */}
        <footer className="bg-slate-900 py-8">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <p className="text-slate-400 text-sm">
              © {new Date().getFullYear()} MSC L'Agence - Tous droits réservés
            </p>
          </div>
        </footer>
      </div>
    );
  }

  // Knowledge Base Home View
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate('/site')}
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour au site
          </button>
          <Link to="/site" className="font-bold text-lg">MSC L'AGENCE</Link>
        </div>
        
        {/* Hero */}
        <div className="max-w-6xl mx-auto px-4 py-16 text-center">
          <Badge className="mb-4 bg-cyan-500/20 text-cyan-300 border-cyan-500/30">
            <Book className="w-3 h-3 mr-1" />
            Centre d'aide
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Comment pouvons-nous vous aider ?
          </h1>
          <p className="text-slate-400 max-w-2xl mx-auto mb-8">
            Trouvez rapidement des réponses à vos questions dans notre base de connaissances.
          </p>
          
          {/* Search */}
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Rechercher dans l'aide..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-14 text-lg bg-white/10 border-white/20 text-white placeholder:text-white/50 rounded-full"
            />
          </div>
          
          {/* Search Results Dropdown */}
          {searchQuery && searchResults.length > 0 && (
            <div className="relative max-w-xl mx-auto mt-2">
              <div className="absolute w-full bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-50">
                {searchResults.slice(0, 5).map(article => (
                  <Link
                    key={article.id}
                    to={`/site/aide/${article.slug}`}
                    className="flex items-center gap-3 p-4 hover:bg-slate-50 transition-colors text-left"
                  >
                    <FileText className="w-5 h-5 text-slate-400 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-slate-900">{article.title}</p>
                      <p className="text-sm text-slate-500">{getCategoryConfig(article.categoryId)?.title}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Categories Grid */}
      <main className="max-w-6xl mx-auto px-4 py-12">
        <h2 className="text-2xl font-bold text-slate-900 mb-8">Explorer par catégorie</h2>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {CATEGORIES.map(category => {
            const Icon = category.icon;
            const articleCount = getArticlesByCategory(category.id).length;
            
            return (
              <Card 
                key={category.id}
                className="group cursor-pointer hover:shadow-lg transition-all border-slate-200 hover:border-slate-300"
                onClick={() => {
                  const element = document.getElementById(`category-${category.id}`);
                  element?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                <CardContent className="p-6">
                  <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center mb-4 bg-gradient-to-br text-white", category.color)}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2 group-hover:text-cyan-600 transition-colors">
                    {category.title}
                  </h3>
                  <p className="text-sm text-slate-600 mb-3">{category.description}</p>
                  <p className="text-xs text-slate-500">{articleCount} article{articleCount > 1 ? 's' : ''}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Featured Articles */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-slate-900 mb-8">Articles populaires</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {ARTICLES.filter(a => FEATURED_ARTICLES.includes(a.id)).map(article => {
              const category = getCategoryConfig(article.categoryId);
              return (
                <Link
                  key={article.id}
                  to={`/site/aide/${article.slug}`}
                  className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-lg hover:border-cyan-300 transition-all group"
                >
                  <Badge className={cn("text-white mb-3 bg-gradient-to-r", category?.color)}>
                    {category?.title}
                  </Badge>
                  <h3 className="font-bold text-slate-900 mb-2 group-hover:text-cyan-600 transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-sm text-slate-600 line-clamp-2">{article.excerpt}</p>
                  <div className="flex items-center gap-2 mt-4 text-xs text-slate-500">
                    <span>{article.views} vues</span>
                    <span>•</span>
                    <span>{article.helpful} utile</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>

        {/* All Categories with Articles */}
        {CATEGORIES.map(category => {
          const articles = getArticlesByCategory(category.id);
          const Icon = category.icon;
          
          return (
            <section key={category.id} id={`category-${category.id}`} className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br text-white", category.color)}>
                  <Icon className="w-5 h-5" />
                </div>
                <h2 className="text-xl font-bold text-slate-900">{category.title}</h2>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                {articles.map(article => (
                  <Link
                    key={article.id}
                    to={`/site/aide/${article.slug}`}
                    className="flex items-start gap-3 p-4 bg-white rounded-xl border border-slate-200 hover:border-cyan-300 hover:shadow-md transition-all group"
                  >
                    <FileText className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-slate-900 group-hover:text-cyan-600 transition-colors">
                        {article.title}
                      </h4>
                      <p className="text-sm text-slate-500 mt-1 line-clamp-1">{article.excerpt}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-300 ml-auto flex-shrink-0 group-hover:text-cyan-500 transition-colors" />
                  </Link>
                ))}
              </div>
            </section>
          );
        })}
      </main>

      {/* Contact CTA */}
      <section className="bg-gradient-to-br from-cyan-500 to-blue-600 py-16">
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <h3 className="text-2xl md:text-3xl font-bold mb-4">
            Vous n'avez pas trouvé votre réponse ?
          </h3>
          <p className="text-cyan-100 mb-8">
            Notre équipe est là pour vous aider. Contactez-nous et nous vous répondrons rapidement.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild className="bg-white text-cyan-600 hover:bg-cyan-50 font-bold">
              <Link to="/site#contact">
                <MessageCircle className="w-4 h-4 mr-2" />
                Nous contacter
              </Link>
            </Button>
            <Button 
              variant="outline" 
              className="border-white/30 text-white hover:bg-white/10"
              onClick={() => {
                const url = clientPortal?.url || '/login';
                if (clientPortal?.openInNewTab) {
                  window.open(url, '_blank');
                } else {
                  window.location.href = url;
                }
              }}
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              {clientPortal?.label || 'Espace client'}
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 py-8">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-slate-400 text-sm">
            © {new Date().getFullYear()} MSC L'Agence - Tous droits réservés
          </p>
          <div className="flex justify-center gap-4 mt-4 text-sm">
            <Link to="/site" className="text-slate-500 hover:text-white transition-colors">Accueil</Link>
            <Link to="/site/blog" className="text-slate-500 hover:text-white transition-colors">Blog</Link>
            <Link to="/site/cgv" className="text-slate-500 hover:text-white transition-colors">CGV</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default KnowledgeBasePage;
